/**
 * Collection of reusable UI components for "mini card"-style components.
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import isString from 'lodash/isString'
import { Button } from 'reactstrap'
import TextTruncate from 'react-text-truncate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import RichTextEditor, { isEmpty, isLinkNote } from 'client/app/js/components/RichTextEditor'
import DefaultCoverImage from 'client/shared/js/components/CoverImage'
import UnsplashBackground from 'client/shared/js/components/UnsplashBackground'
import styles from './MiniCardUIComponents.module.scss'

export const Title = ({ children, className, line = 2, size = 'xs', mb = '1', tagname = 'h3' }) => (
  React.createElement(
    tagname,
    {
      title: children,
      className: cx(`font-${size} mb-${mb}`, className)
    },
    <TextTruncate line={line} text={children} />
  )
)

Title.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  line: PropTypes.number,
  size: PropTypes.string,
  mb: PropTypes.string,
  tagname: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])
}

export const Body = ({ className, children }) => (
  <div className={cx(styles['body'], className)}>
    {children}
  </div>
)

Body.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
}

export const NoteBody = ({ place }) => {
  const isUrlOnly = useMemo(() => isLinkNote(place.notes), [place.notes])

  if (isEmpty(place.notes)) {
    return null
  }

  const bodyClass = cx({
    [styles['note-body']]: !isUrlOnly,
    [styles['link-body']]: isUrlOnly
  })

  return (
    <Body className={bodyClass}>
      <RichTextEditor
        className={cx(styles['note'])}
        value={place.notes}
        editable={false} />
      <div className={cx(styles['gradient-overlay'])} />
    </Body>
  )
}

NoteBody.propTypes = {
  place: PropTypes.object
}

/**
 * Creates an element that preserves aspect ratio
 * @param {String} [props.element='div'] The DOM element
 * @param {Number} [props.x=1] The x aspect ratio
 * @param {Number} [props.y=1] The y aspect ratio
 */
export const AspectRatioBox = ({ element, x, y, children, ...props }) => {
  const outerStyle = useMemo(() => ({
    position: 'relative',
    paddingTop: `${(x / y * 100).toFixed(3)}%`
  }), [x, y])

  const innerStyle = useMemo(() => ({
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%'
  }), [])

  const outer = (children) => React.createElement(element, { style: outerStyle }, children)
  const inner = (children) => React.createElement(element, { style: innerStyle, ...props }, children)

  return outer(inner(children))
}

AspectRatioBox.propTypes = {
  element: PropTypes.string,
  x: PropTypes.number,
  y: PropTypes.number,
  children: PropTypes.node
}

AspectRatioBox.defaultProps = {
  element: 'div',
  x: 1,
  y: 1
}

export const BtnLink = ({ children, className, title, onClick }) => (
  <Button
    className={cx(styles['btn-link'], 'transparent', className)}
    title={title}
    onClick={onClick}
    color='link'>
    {children}
  </Button>
)

BtnLink.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func
}

export const Icon = ({ children, className, title, onClick }) => (
  <Button
    className={cx(styles['icon-btn'], 'round', className)}
    title={title}
    onClick={onClick}
    color='light-blue'
    size='xs'>
    {children}
  </Button>
)

Icon.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func
}

export const PlusIcon = (props) => (
  <Icon {...props}>
    <FontAwesomeIcon icon={['fal', 'plus']} className='font-inherit' />
  </Icon>
)

export const CalendarIcon = (props) => (
  <Icon {...props}>
    <FontAwesomeIcon icon={['fal', 'calendar-plus']} className='font-inherit' />
  </Icon>
)

export const CoverImage = ({
  className,
  photo,
  defaultImage = '/static/assets/img/illustrations/best-place.svg'
}) => {
  if (!isString(photo) && !photo?.urls) {
    return (<img src={defaultImage} className='w-100 mh-100 p-1 align-self-end' />)
  } else if (isString(photo)) {
    return (
      <DefaultCoverImage
        className={className}
        photoUrl={photo}
        backgroundFilter='top' />
    )
  } else if (photo.source === 'unsplash') {
    return (
      <UnsplashBackground
        backgroundPhoto={photo}
        backgroundFilter='top'
        disableLinks
        disableAttribution />
    )
  }

  return (
    <DefaultCoverImage
      className={className}
      photoUrl={photo?.urls?.regular || photo}
      backgroundFilter='top' />
  )
}

CoverImage.propTypes = {
  className: PropTypes.string,
  photo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  defaultImage: PropTypes.string
}
